import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PageRequest } from '../../lib/api/paging';
import {
  getCityLevers,
  getLocaleLevers,
  getStripePayout,
  listAuditLog,
  listCities,
  listRegistrations,
  listStripePayouts,
  markBankVerified,
  markLoadedInStripe,
  patchLocaleLevers,
  registerCity,
  type LeverValue,
  type RegisterCityInput,
} from './leversApi';

export const leverKeys = {
  all: ['money-levers'] as const,
  locale: (id: string) => [...leverKeys.all, 'locale', id] as const,
  city: (id: string, localeId?: string) => [...leverKeys.all, 'city', id, localeId ?? ''] as const,
  cities: (p: object) => [...leverKeys.all, 'cities', p] as const,
  registrations: (p: object) => [...leverKeys.all, 'registrations', p] as const,
  audit: (p: object) => [...leverKeys.all, 'audit', p] as const,
  payouts: (p: object) => [...leverKeys.all, 'stripe-payouts', p] as const,
  payout: (id: string) => [...leverKeys.all, 'stripe-payout', id] as const,
};

/** Reads show their errors inline, so they are silent. */
export function useLocaleLevers(localeId: string | undefined) {
  return useQuery({
    queryKey: leverKeys.locale(localeId ?? ''),
    queryFn: ({ signal }) => getLocaleLevers(localeId!, signal),
    enabled: !!localeId,
    meta: { silent: true },
  });
}

export function useCityLevers(cityId: string | undefined, localeId?: string) {
  return useQuery({
    queryKey: leverKeys.city(cityId ?? '', localeId),
    queryFn: ({ signal }) => getCityLevers(cityId!, localeId, signal),
    enabled: !!cityId,
    meta: { silent: true },
  });
}

export function useCities(params: PageRequest & { homeRule?: boolean; stateProvince?: string; searchTerm?: string }) {
  return useQuery({
    queryKey: leverKeys.cities(params),
    queryFn: ({ signal }) => listCities(params, signal),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

export function useRegistrations(params: PageRequest & { jurisdictionType?: string; status?: string }) {
  return useQuery({
    queryKey: leverKeys.registrations(params),
    queryFn: ({ signal }) => listRegistrations(params, signal),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

export function useAuditLog(
  params: PageRequest & { entityType?: string; entityId?: string; fieldName?: string; from?: string; to?: string },
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: leverKeys.audit(params),
    queryFn: ({ signal }) => listAuditLog(params, signal),
    enabled: opts.enabled ?? true,
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

export function useStripePayouts(
  params: PageRequest & {
    status?: string;
    reconciliationStatus?: string;
    bankVerified?: boolean;
    from?: string;
    to?: string;
  },
) {
  return useQuery({
    queryKey: leverKeys.payouts(params),
    queryFn: ({ signal }) => listStripePayouts(params, signal),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

export function useStripePayout(id: string) {
  return useQuery({
    queryKey: leverKeys.payout(id),
    queryFn: ({ signal }) => getStripePayout(id, signal),
    meta: { silent: true },
  });
}

/**
 * Writes refresh every money read — levers feed the dashboards. They are
 * silent: a refusal (missing reason, out-of-range value) is shown in the form.
 */
function useLeverMutation<A, R>(fn: (args: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leverKeys.all });
      void qc.invalidateQueries({ queryKey: ['money'] });
    },
    meta: { silent: true },
  });
}

export const usePatchLocaleLevers = (localeId: string) =>
  useLeverMutation(({ changes, reason }: { changes: Record<string, LeverValue>; reason: string }) =>
    patchLocaleLevers(localeId, changes, reason),
  );
export const useRegisterCity = (cityId: string) =>
  useLeverMutation((input: RegisterCityInput) => registerCity(cityId, input));
export const useMarkLoadedInStripe = () =>
  useLeverMutation(({ id, reason }: { id: string; reason: string }) => markLoadedInStripe(id, reason));
export const useMarkBankVerified = () =>
  useLeverMutation(({ id, reason }: { id: string; reason: string }) => markBankVerified(id, reason));
